import { useCallback, useContext, useEffect, useRef } from "react";
import { __RouterContext } from "react-router";

import { FlightPhaseEnum } from "@skydio/pbtypes/pbtypes/gen/common/flight_phase_enum_pb";

import { getFlightId, getFlightPhaseId, getIsMostRecentVehiclePilot } from "state/pilot/selectors";
import { useVehicleId } from "state/pilot/utils";
import { getRoomName, getTeleopSessionId } from "state/teleop/selectors";
import { trackAnalytic, trackUserAction } from "utils/trackEvent";

import { useSelector } from "./redux";

type TrackUserAction = (name: string, payload?: object) => void;
type TrackAnalytic = (name: string, payload?: object) => void;

/**
 * Track a streaming event, with common properties like session ID, flight ID, and
 * vehicle ID. The returned callback will not change between renders.
 */
export function useTrackStreamingEvent(): [TrackUserAction, TrackAnalytic] {
  const common = useRef<{
    teleopSessionId?: string;
    roomName?: string;
    flightId?: string;
    vehicleId?: string;
    flightPhase?: string;
    surface?: string;
  }>({});

  const teleopSessionId = useSelector(getTeleopSessionId);
  const roomName = useSelector(getRoomName);
  const flightId = useSelector(getFlightId);
  const flightPhase = useSelector(getFlightPhaseId);
  const routerContext = useContext(__RouterContext);
  const isPilot = useSelector(getIsMostRecentVehiclePilot);
  const vehicleId = useVehicleId();
  const pathname =
    routerContext?.location?.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "");

  /**
   * NOTE(trey): the implementation of this hook is admittedly odd. this approach ensures the track
   * callback will not change between renders. we could bake the common parameters into the returned
   * callback, but this would trigger updates much more frequently. given the current setup of LiveKit
   * components (namely LivekitSkybusProvider and setupLivekitSkybus), we need to minimize updates to
   * avoid turning over the WebRTC connection. once these components have been refactored, we can
   * probably simplify this to a simple hook with no shenanigans. one potential downside of the
   * current approach is that we could log an event with stale parameters, but this isn't a big deal
   * in this case because all the common parameters change very infrequently.
   */
  useEffect(() => {
    // Determine surface based on pathname and pilot state
    let surface: string | undefined;

    if (pathname.includes("/fleet/flights")) {
      surface = "fleetvehicle";
    } else if (pathname.includes("/flight/live/")) {
      surface = isPilot ? "teleop" : "livestream";
    } else if (pathname.includes("/flight/vehicle-stream/")) {
      surface = "livestream";
    } else if (pathname.startsWith("/shared_link/")) {
      surface = "readylink";
    }

    common.current.teleopSessionId = teleopSessionId;
    common.current.roomName = roomName;
    common.current.surface = surface;
    common.current.flightId = flightId;
    common.current.vehicleId = vehicleId ?? undefined;
    common.current.flightPhase = Object.entries(FlightPhaseEnum.Enum).find(
      ([, v]) => v === flightPhase
    )?.[0];
  }, [teleopSessionId, roomName, flightId, flightPhase, isPilot, vehicleId, pathname]);

  const trackUserActionEvent = useCallback((name: string, payload?: object) => {
    return trackUserAction(name, { ...common.current, ...payload });
  }, []);

  const trackAnalyticEvent = useCallback(
    (name: string, payload?: object) => trackAnalytic(name, { ...common.current, ...payload }),
    []
  );

  return [trackUserActionEvent, trackAnalyticEvent];
}
